import { LitElement, html, css, unsafeCSS } from "./lit-all.min.js";

const sharedStyles = css`
	:host {
		font: var(--text-md);
	}

	 h1, h2, h3, h4, p, ul, select, button {
		margin: 0;
		padding: 0;
		background: none;
		border: none;
		font: inherit;
		color: inherit;
	}
`;

const themeStylesheet = [...document.styleSheets].find(sheet => sheet.href?.includes('theme.css'));
const buttonRules = [...themeStylesheet.cssRules].filter(rule => rule.cssText?.includes('.button'));
const buttonStyles = unsafeCSS(buttonRules.map(rule => rule.cssText).join('\n'));

class OkendoReviewsProvider {
	constructor() {
		this.userId = theme.storeLocation == 'AU' ? 'a3602b8e-9d05-4cb8-a700-0b0b0096f048' :
									theme.storeLocation == 'US' ? 'ebb9e0f0-c4de-4865-be89-2cfae6224d4b' :
									theme.storeLocation == 'UK' ? 'd2785c22-afc7-417f-a7df-011ec3caf480' : 
									null;
		this.apiUrl = `https://api.okendo.io/v1/stores/${this.userId}`;
		this.fetchCache = new Map();

		this.fetchedReviews = new Map();
		this.fetchedReviewsGrouped = new Map();
		this.nextUrls = new Map();
		this.maxReviewCount = new Map();
	}

	async #get(endpoint, { productId, groupId}) {
		const url = productId ? `${this.apiUrl}/products/shopify-${productId}/${endpoint}` : `${this.apiUrl}/collections/${groupId}/${endpoint}`;

		if (this.fetchCache.has(url)) return this.fetchCache.get(url);
		else {
			const promise = fetch(url).then(resp => resp.json());
			this.fetchCache.set(url, promise);
			return promise;
		}
	}

	async #getAggregate({ productId, groupId }) {
		const aggregateResp = await this.#get('review_aggregate', { productId, groupId });
		const aggregate = aggregateResp.reviewAggregate;

		return {
			reviewCount: aggregate.reviewCount || 0,
			recsPercentage: aggregate.ratingAndReviewCount > 0 ? Math.round((aggregate.recommendationCount / aggregate.ratingAndReviewCount) * 100) : null,
			averageRating: aggregate.ratingAndReviewCount > 0? aggregate.ratingAndReviewValuesTotal / aggregate.ratingAndReviewCount : null,
			attributeRatings: aggregate.attributeRatings ?? [],
		}
	}

	async #getAggregateGrouped({ productIds }) {
		const aggregates = (await Promise.all(productIds.map(id => this.#getAggregate({ productId: id }))));
		const aggregatesWithReviews = aggregates.filter(a => a.reviewCount > 0);
		
		return {
			reviewCount: aggregates.reduce((acc, curr) => acc + curr.reviewCount, 0),
			recsPercentage: Math.round(aggregatesWithReviews.reduce((acc, curr) => acc + curr.recsPercentage, 0) / aggregatesWithReviews.length),
			averageRating: aggregatesWithReviews.reduce((acc, curr) => acc + curr.averageRating, 0) / aggregatesWithReviews.length,
			attributeRatings: []
		}
	}

	async getProductAggregate(productId) {
		return this.#getAggregate({ productId });
	}

	async getProductAggregateGrouped(productIds) {
		return this.#getAggregateGrouped({ productIds });
	}

	async getGroupAggregate(groupId) {
		return this.#getAggregate({ groupId });
	}

	async #getReviews({ productId, groupId, sortBy, limit, page }) {
		page = Math.max(1, page);

		const key = productId ? `${productId}-${sortBy}` : `${groupId}-${sortBy}`;

		if (!this.fetchedReviews.has(key)) {
			const json = await this.#get(`reviews?orderBy=${sortBy}&limit=${limit}`, { productId, groupId });
			this.fetchedReviews.set(key, json.reviews);
			this.nextUrls.set(key, json.nextUrl);
		}

		const fetchedReviews = this.fetchedReviews.get(key);
		let nextUrl = this.nextUrls.get(key);

		const targetLength = page * limit;
		const requiredLength = (page - 1) * limit + 1;

		if (this.maxReviewCount.has(key) && fetchedReviews.length < this.maxReviewCount.get(key)) return [];

		while (fetchedReviews.length < targetLength && nextUrl) {
			const fetchUrl = new URL(`https://api.okendo.io/v1${nextUrl}`);
			fetchUrl.searchParams.set('limit', 25);

			const resp = await fetch(fetchUrl.toString());
			const json = await resp.json();

			fetchedReviews.push(...json.reviews);
			nextUrl = json.nextUrl;
			this.nextUrls.set(key, nextUrl);
		}

		if (fetchedReviews.length < requiredLength) {
			this.maxReviewCount.set(key, fetchedReviews.length);
			return [];
		}
		return fetchedReviews.slice(targetLength - limit, targetLength);
	}

	async #getReviewsGrouped({ productIds, sortBy, limit, page }) {
		page = Math.max(1, page);

		const key = `${productIds.join(',')}-${sortBy}`;

		if (!this.fetchedReviewsGrouped.has(key)) this.fetchedReviewsGrouped.set(key, []);

		const fetchedReviews = this.fetchedReviewsGrouped.get(key);

		const targetLength = page * limit;
		const requiredLength = (page - 1) * limit + 1;

		for (const id of productIds) {
			const reviews = await this.#getReviews({ productId: id, sortBy, limit, page });
			fetchedReviews.push(...reviews.filter(r => !fetchedReviews.includes(r)));
		}

		const sortType = sortBy.split(' ')[0];
		const sortOrder = sortBy.split(' ')[1] === 'asc' ? -1 : 1;
		const sortKey = 
			sortType === 'date' ? 'dateCreated' 
		: sortType === 'rating' ? 'rating' 
		: 'helpfulCount';

		const sortKeyFunc = r => sortType == 'date' ? new Date(r.dateCreated) : r[sortKey];

		fetchedReviews.sort((a, b) => (sortKeyFunc(b) - sortKeyFunc(a)) * sortOrder);

		if (fetchedReviews.length < requiredLength) return [];
		return fetchedReviews.slice(targetLength - limit, targetLength);
	}

	async getProductReviews(productId, { sortBy = 'date desc', limit = 5, page = 1 } = {}) {
		return this.#getReviews({ productId, sortBy, limit, page });
	}

	async getProductReviewsGrouped(productIds, { sortBy = 'date desc', limit = 5, page = 1 } = {}) {
		return this.#getReviewsGrouped({ productIds, sortBy, limit, page });
	}

	async getGroupReviews(groupId, { sortBy = 'date desc', limit = 5, page = 1 } = {}) {
		return this.#getReviews({ groupId, sortBy, limit, page });
	}

	async upvote(reviewId) {
		await fetch(`${this.apiUrl}/reviews/${reviewId}/vote`, {
			method: 'POST',
			body: JSON.stringify({ voteType: 'upvote' })
		});
	}

	async upvoteCancel(reviewId) {
		await fetch(`${this.apiUrl}/reviews/${reviewId}/vote`, {
			method: 'POST',
			body: JSON.stringify({ voteType: 'upvote-cancel' })
		});
	}

	getProductReviewLink(productId) {
		return `https://reviews.okendo.io/stores/${this.userId}/review?productId=shopify-${productId}&locale=en`
	}
}

const provider = new OkendoReviewsProvider();

class ProductReviews extends LitElement {
	static properties = {
		heading: { type: String },
		aggregate: { type: Object },
		productName: { type: String },
		productIds: { type: Array },
		groupIds: { type: Array },
		tabNames: { type: Array },
		sortBy: { type: String },
		productId: { type: String },
		groupId: { type: String },
	}

	constructor() {
		super();

		this.productIds = [];
		this.productId = '';
		this.productName = '';

		this.groupIds = [];	
		this.groupId = '';

		this.tabNames = [];

		this.sortBy = 'date desc';

		this.aggregate = null;

		this.heading = 'Customer Reviews';
		this.recsPercentage = 0;
		this.reviewCount = 0;
		this.averageRating = 0;
		this.attributeRatings = [];

		this.reviewCounts = {};

		this.numberFormatter = new Intl.NumberFormat();
	}

	async loadAggregate() {
		let aggregates = [];

		if (this.productIds.length > 0) {
			const ids = !this.productId ? this.productIds : [this.productId];
			aggregates = await Promise.all(ids.map(id => provider.getProductAggregate(id)));
			aggregates.forEach((agg, idx) => this.reviewCounts[ids[idx]] = agg.reviewCount || 0);
		}
		else if (this.groupIds.length > 0) aggregates = [await provider.getGroupAggregate(this.groupId)];

		aggregates = aggregates.filter(a => a.reviewCount);

		this.aggregate = {
			reviewCount: aggregates.reduce((acc, curr) => acc + curr.reviewCount, 0),
			averageRating: aggregates.reduce((acc, curr) => acc + curr.averageRating, 0) / aggregates.length,
			recsPercentage: Math.round(aggregates.reduce((acc, curr) => acc + curr.recsPercentage, 0) / aggregates.length),
			attributeRatings: aggregates.length > 0 ? aggregates[0].attributeRatings : [],
		}

		if (this.aggregate.reviewCount == 0) this.hidden = true;
	}

	scrollToTop() {
		const top = this.shadowRoot.querySelector('.top');
		if (top.getBoundingClientRect().top < 0) top.scrollIntoView({ behavior: 'instant' });
	}

	willUpdate(changedProperties) {
		if (changedProperties.has('productIds') && this.productIds.length > 0) {
			this.loadAggregate();
		}

		if (changedProperties.has('productId') || changedProperties.has('groupId')) {
			this.loadAggregate();
		}

		if (changedProperties.has('groupIds') && this.groupIds.length > 0) {
			this.groupId = this.groupIds[0];
			this.loadAggregate();
		}

		if (changedProperties.has('aggregate') && this.aggregate) {
			if (this.aggregate.reviewCount > 0) {
				this.recsPercentage = this.aggregate.recsPercentage;
				this.reviewCount = this.aggregate.reviewCount;
				this.averageRating = this.aggregate.averageRating;
				this.attributeRatings = this.aggregate.attributeRatings;
			}
		}
	}

	render() {
		let content = null;

		if (!this.aggregate) content = html`
			<h2>${this.heading}</h2>
			<slot></slot>
		`;

		else if (this.reviewCount === 0) content =  html`
			<h2>${this.heading}</h2>
			<div class="empty">
				<p>No reviews yet, write one now?</p>
				<a target="_blank" class="button button--secondary" href="${provider.getProductReviewLink(this.productId)}">Write a review</a>
			</div>
		`;

		else content = html`
			<h2>
				${this.heading}
				${this.productIds.length > 1 ? html`
				<div class="view">
					for 
					<reviews-dropdown 
					.options=${[
						{ value: '', name: `All ${this.productName} items` },
						...this.productIds.map((id, idx) => ({ value: id, name: this.tabNames[idx], disabled: !this.reviewCounts[id] }))
					]}
					.selected=${this.productId}
					@change=${e => this.productId = e.detail.value}
					></reviews-dropdown>
				</div>
			` : ''}
			</h2>
			${this.groupIds.length > 1 ? html`
				<ul class="tabs">
					${this.groupIds.map((id, idx) => html`
						<button class="tab ${this.groupId == id ? 'active' : ''}" @click=${() => this.groupId = id}>
							${idx == 0 ? 'All' : this.tabNames[idx]}
						</button>
					`)}
				</ul>
			`: ''}
			<div class="header">
				<div class="summary">
					<div class="summary-item">
						<p class="summary-text">${this.numberFormatter.format(this.reviewCount)}</review-stars></p>
						<h3 class="summary-title">Total reviews</h3>
					</div>
					<div class="summary-item">
						<p class="summary-text">${this.averageRating.toFixed(1)} <review-stars stars=${this.averageRating}></review-stars></p>
						<h3 class="summary-title">Average rating</h3>
					</div>
				</div>

				<p>${this.recsPercentage}% of users recommend ${this.productId ? 'this product' : 'these products'}</p>

				${this.attributeRatings.length > 0 ? html`
					<div class="attributes">
						${this.attributeRatings.map(attr => html`
							<review-attribute-rating 
								title=${attr.title} 
								rating=${attr.value} 
								minLabel=${attr.minLabel} 
								maxLabel=${attr.maxLabel}>
							</review-attribute-rating>
						`)}
					</div>
				` : ''}
			</div>
			<div class="top"></div>
			<div class="reviews-header">
				<p class="count">${this.numberFormatter.format(this.reviewCount)} reviews</p>
				<div class="sort">
					<span>Sort by:</span>
					<select class="sort" @change=${e => this.sortBy = e.target.value}>
						<option value="date desc">Most Recent</option>
						<option value="date asc">Oldest</option>
						<option value="rating desc">Highest Rating</option>
						<option value="rating asc">Lowest Rating</option>
						<option value="helpful desc">Most Helpful</option>
						<option value="helpful asc">Least Helpful</option>
					</select>
				</div>
			</div>
		`;

		return html`
			${content}
			<product-review-list
				.productIds=${this.productId ? [this.productId] : this.productIds}
				groupId=${this.groupId}
				sortBy=${this.sortBy}
				@changed=${this.scrollToTop}
				?hidden=${!this.aggregate || this.reviewCount === 0}
			></product-review-list>
		`;
	}

	static styles = css`
		${sharedStyles}

		${buttonStyles}

		:host {
			display: block;
		}

		:host([hidden]) {
			display: none;
		}

		h2 {
			font: var(--title-sm);
			color: var(--color-text-primary);
			margin-bottom: var(--padding-sm);
			display: flex;
			gap: 0.5ch;
			align-items: center;
		}

		.empty {
			display: flex;
			flex-direction: column;
			gap: var(--padding-sm);
			align-items: start;
		}

		.header {
			display: flex;
			flex-direction: column;
			gap: var(--padding-sm);
			margin-bottom: var(--padding-md);
		}

		.header p {
			white-space: nowrap;
		}

		.summary {
			display: flex;
			gap: var(--padding-md);
		}

		.summary-item {
			display: flex;
			flex-direction: column;
		}

		.summary-text {
			display: flex;
			font: var(--title-xl);
			color: var(--color-text-primary);
			align-items: center;
			gap: 8px;
		}

		.attributes {
			display: flex;
			flex-wrap: wrap;
			row-gap: var(--padding-sm);
			column-gap: var(--padding-md);
		}

		@media screen and (min-width: 992px) {
			.header {
				flex-direction: row;
				justify-content: space-between;
				align-items: center;
				flex-wrap: wrap;
			}

			.attributes {
				width: min-content;
			}

			.summary-title {
				font: var(--text-lg);
			}
		}

		@media screen and (min-width: 1100px) {
			.attributes {
				width: auto;
			}
		}
		
		.top {
			scroll-margin-top: var(--header-height);
		}

		.reviews-header {
			display: flex;
			justify-content: space-between;
			padding-block: var(--padding-xs);
			border-bottom: 1px solid var(--color-border-border-primary, #E1E1E1);
			background: white;
			position: sticky;
			top: var(--header-height);
			z-index: 5;
			flex-wrap: wrap;
			gap: var(--padding-sm);
			align-items: center;
		}

		.count {
			font: var(--text-sm);
		}

		.reviews, .pagination {
			transition: opacity 350ms;
		}

		.sort {
			display: flex;
			align-items: center;
			gap: 4px;
			font: var(--text-sm);
		}

		.view {
			display: flex;
			align-items: center;
			gap: 6px;
		}

		select {
			appearance: none;
			padding-right: 24px;
			background-image: var(--icon-arrow-down);
			background-repeat: no-repeat;
			background-position: right center;
			background-size: 24px;
		}

		select option {
			font: var(--text-md);
		}

		.tab {
		  padding: var(--padding-xs) var(--padding-sm);
			border-bottom: 1px solid var(--color-border-border-primary, #E1E1E1);
			cursor: pointer;
			white-space: nowrap;
		}

		@media (hover: hover) {
			.tab:hover {
				color: var(--color-text-primary);
			}
		}

		.tab.active {
			color: var(--color-text-primary);
  		border-bottom: 1px solid #212121;	
		}

		.tabs {
			display: flex;
			overflow-x: auto;
			margin-bottom: var(--padding-sm);
			margin-inline: -32px;
			padding-inline: 32px;
			scrollbar-width: none;
  		-ms-overflow-style: none;
		}

		.tabs::-webkit-scrollbar {
			width: 0;
			height: 0;
		}
		
		.tabs .tab:first-child {
			margin-left: auto;
		}
		
		.tabs .tab:last-child {
			margin-right: auto;
		}

		@media screen and (max-width: 512px) {
			.tabs {
				order: 5;
			}
		}

		@media screen and (min-width: 992px) {
			.tabs {
				margin-bottom: var(--padding-md);
			}
		}
	`;
}

customElements.define('product-reviews', ProductReviews);


class ProductReviewList extends LitElement {
	static properties = {
		productIds: { type: Array },
		groupId: { type: String },
		sortBy: { type: String },
		reviews: { type: Array },
		refreshing: { type: Boolean, reflect: true },
		page: { type: Number },
		pages: { type: Number },
	}

	constructor() {
		super();

		this.productIds = [];
		this.groupId = null;	
		this.reviews = [];

		this.sortBy = 'date desc';
		this.limit = 5;

		this.page = 1;
		this.pages = 1;
	}

	async loadPageCount() {
		this.pages = 1;
		const aggregate = this.groupId ? await provider.getGroupAggregate(this.groupId) : await provider.getProductAggregateGrouped(this.productIds);
		this.pages = Math.min(Math.ceil(aggregate.reviewCount / this.limit), this.page + 50);
	}

	async loadReviews() {
		this.refreshing = true;
		const params =  { sortBy: this.sortBy, limit: this.limit, page: this.page };
		this.reviews = this.groupId ? await provider.getGroupReviews(this.groupId, params) : await provider.getProductReviewsGrouped(this.productIds, params);
		this.refreshing = false;
	} 

	willUpdate(changedProperties) {
		if (changedProperties.has('sortBy') || changedProperties.has('productIds') || changedProperties.has('groupId')) {
			this.page = 1;
			this.loadPageCount();
			this.loadReviews();
		}

		if (changedProperties.has('page')) {
			this.loadReviews();
		}

		if (changedProperties.has('reviews') && changedProperties.get('reviews')?.length > 0) {
			this.dispatchEvent(new Event('changed'));
		}
	}

	async setPage(page) {
		this.page = Math.max(1, page);
	}

	render() {
		if (this.reviews.length === 0) return html`
			Loading...
		`;

		return html`
			<ul class="reviews">
				${this.reviews.map(review => html`
					<product-review role="listitem" .review=${review}></product-review>
				`)}
			</ul>
			${this.renderPagination()}
		`;
	}

	renderPagination() {
		if (this.pages <= 1) return;
		const pages = [];
		const visibleRange = 2;

		for (let i = this.page - visibleRange; i <= this.page + visibleRange; i++) {
			if (i < 1 || i > this.pages) continue;
			pages.push(i);
		}

		return html`
			<div class="pagination">
				${this.page != 1 ? html`<button class="previous" @click=${() => this.setPage(this.page - 1)}>Previous</button>` : ''}
				${this.page > 1 + visibleRange ? html`<button class="first" @click=${() => this.setPage(1)}>1</button><span>…</span>` : ''}
				${pages.map(page => html`
					<button class="${this.page == page ? 'active': ''}" @click=${() => this.setPage(page)} ?disabled=${this.page === page}>${page}</button>
				`)}
				${this.page < this.pages - visibleRange ? html`</button><span>…</span><button class="last" @click=${() => this.setPage(Math.min(this.pages, this.page + 50))}>${Math.min(this.pages, this.page + 50)}</button>` : ''}
				${this.page < this.pages - 1 ? html`<button class="next" @click=${() => this.setPage(this.page + 1)}>Next</button>` : ''}
			</div>
		`
	}

	static styles = css`
		${sharedStyles}
		${buttonStyles}

		:host([refreshing]) :is(.reviews, .pagination) {
			opacity: 0.3;
			pointer-events: none;
		}

		.pagination {
			display: flex;
			justify-content: center;
		}

		.pagination * {
			display: flex;
			align-items: center;
			min-width: 40px;
			min-height: 36px;
			justify-content: center;
			margin-top: var(--padding-md);
			gap: var(--padding-xs);
		}

		.pagination button {
			padding-inline: var(--padding-xs);
			cursor: pointer;
		}

		.pagination button.active {
			border-radius: 2px;
			border: 1px solid var(--color-border-icon-tertiary, #767676);
		}

		.next::after, .previous::after {
			content: '';
			display: block;
			width: 24px;
			height: 24px;
			background-image: var(--icon-arrow-right);
			background-size: contain;
		}

		.previous::after {
			transform: scaleX(-1);
			order: -1;
		}
	`
}

customElements.define('product-review-list', ProductReviewList);

class ProductReview extends LitElement {
	static properties = {
		review: { type: Object },
		voting: { type: Boolean, reflect: true },
	}

	constructor() {
		super();

		this.review = null;
		this.reviewer;
		this.voted = false;
		this.voting = false;
	}

	willUpdate(changedProperties) {
		if (changedProperties.has('review')) {
			this.reviewer = this.review.reviewer;
		}
	}

	formatDate(dateString) {
		const date = Date.parse(dateString);
		const now = Date.now();
		const diff = now - date;

		const hour = 1000 * 60 * 60;
		const day = hour * 24;
		const week = day * 7;
		const month = day * 30;
		const year = day * 365;

		if (diff < hour) return 'Just now';
		else {
			let [count, unit] = '';
			
			if (diff < day) [count, unit] = [Math.floor(diff / hour), 'hour'];
			else if (diff < week) [count, unit] = [Math.floor(diff / day), 'day'];
			else if (diff < month) [count, unit] = [Math.floor(diff / week), 'week'];
			else if (diff < year) [count, unit] = [Math.floor(diff / month), 'month'];
			else [count, unit] = [Math.floor(diff / year), 'year'];

			return `${count} ${unit}${count > 1 ? 's' : ''} ago`;
		}
	}

	async vote() {
		this.voting = true;

		if (this.voted) {
			await provider.upvoteCancel(this.review.reviewId);
			this.review.helpfulCount--;
			this.voted = false;
		}
		else {
			await provider.upvote(this.review.reviewId);
			this.review.helpfulCount++;
			this.voted = true;
		}

		this.voting = false;
	}

	render() {
		if (!this.review) return html``;

		return html`
			<div class="header">
				<p class="reviewer ${this.reviewer.isVerified ? 'verified' : ''}">
					${this.reviewer.displayName} ${this.reviewer.location?.country?.name ? `from ${this.reviewer.location.country.name}` : ''}
				</p>
				<p class="recommendation ${this.review.isRecommended ? 'positive' : 'negative'}">
					${this.review.isRecommended ? 'I recommend this product' : 'I do not recommend this product'}
				</p>
			</div>
			<div class="details">
				<review-stars rating=${this.review.rating}></review-stars>
				<h3 class="title">${this.review.title}</h3>
				<p class="date">${this.formatDate(this.review.dateCreated)}</p>
			</div>
			<p class="content">${this.review.body}</p>
			${this.review.reply ? html`
				<div class="response">
					<img class="response-avatar" loading="lazy" src="${this.review.reply.avatarUrl}" />
					<p class="response-date">${this.formatDate(this.review.reply.dateCreated)}</p>
					<h4 class="response-title">Response from ${this.review.reply.title}</h4>
					<div class="response-content">${document.createRange().createContextualFragment(this.review.reply.body)}</div>
				</div>
			`: ''}
			${this.review.productName.includes('Build Your Own') ? '' : html`
				<div class="product">
					<img class="product-image" src="${this.review.productImageUrl}" loading="lazy" />
					<div class="product-details">
						<h4>Reviewing:</h4>
						<p><a href="${this.review.productUrl}">${this.review.productName}</a></p>
					</div>
				</div>
			`}
			<p class="helpful">Was this review helpful? <button class="helpful-button" @click=${this.vote} ?disabled=${this.voting}>(${this.review.helpfulCount})</button></p>
		`;
	}


	static styles = css`
		${sharedStyles}

		:host {
			display: flex;
			flex-direction: column;
			gap: var(--padding-sm);
			padding-block: var(--padding-md);
			border-bottom: 1px solid var(--color-border-border-primary, #E1E1E1);
		}

		.header {
			display: flex;
			justify-content: space-between;
			flex-wrap: wrap;
			gap: var(--padding-xs);
		}

		.reviewer, .recommendation {
			display: flex;
			align-items: center;
			gap: 4px;
			font: var(--text-sm);
		}

		.reviewer.verified::before, .recommendation::before {
			content: '';
			display: block;
			width: 16px;
			height: 16px;
			background-size: contain;
		}

		.reviewer.verified::before {
			background-image: var(--icon-verified);
		}

		.recommendation.positive::before {
			background-image: var(--icon-thumbs-up);
		}

		.recommendation.negative::before {
			background-image: var(--icon-thumbs-down);
		}

		.details {
			display: flex;
			flex-direction: column;
			gap: 4px;
		}

		review-stars {
			margin-bottom: 4px;
		}

		.title {
			font: var(--text-lg);
			color: var(--color-text-primary);
		}

		.date {
			font: var(--text-sm);
		}

		.response {
			display: grid;
			grid-template-areas: 
				"avatar header" 
				"avatar content";
			padding-left: var(--padding-xs);
			border-left: 1px solid var(--color-border-border-primary, #E1E1E1);
			row-gap: 4px;
			column-gap: 8px;
			grid-template-columns: min-content 1fr;
		}

		.response-avatar {
			grid-area: avatar;
			width: 48px;
			height: 48px;
		}

		.response-title, .response-date {
			font: var(--text-sm);
			grid-area: header;
		}

		.response-title {
			justify-self: start;
		}

		.response-date {
			justify-self: end;
		}

		.response-content {
			grid-area: content;
		}

		.response-content br {
			display: block;
			margin-top: 4px;
		}

		.product {
			display: flex;
			align-items: center;
			font: var(--text-sm);
			gap: var(--padding-xs);
			align-self: start;
			position: relative;
		}

		.product a {
			color: inherit;
			text-decoration: none;
		}

		.product a::after {
			content: '';
			display: block;
			position: absolute;
			top: 0;
			left: 0;
			bottom: 0;
			right: 0;
			z-index: 5;
		}

		.product-image {
			width: 48px;
			height: auto;
			aspect-ratio: 0.8;
			background: rgba(0, 0, 0, 0.03);
		}

		.helpful {
			font: var(--text-sm);
			display: flex;
			align-items: center;
			gap: var(--padding-xs);
		}

		.helpful-button {
			padding: 8px;
			margin: -8px;
			display: flex;
			align-items: center;
			gap: 4px;
			cursor: pointer;
			opacity: 0.8;
		}

		.helpful-button[disabled] {
			pointer-events: none;
			opacity: 0.5;
			transition: opacity 0.2s;
		}

		@media (hover: hover) {
			.helpful-button:hover {
				opacity: 0.9;
			}
		}

		.helpful-button:active {
			opacity: 1;
		}

		.helpful-button::before {
			content: '';
			display: block;
			width: 12px;
			height: 12px;
			background-image: var(--icon-thumbs-up);
			background-size: contain;
		}
	`;
}

customElements.define('product-review', ProductReview);


class ReviewStars extends LitElement {
	static properties = {
		rating: { type: Number },
	}

	constructor() {
		super();

		this.rating = 5;
	}

	static empty = html`
		<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M4.29369 4.11799L5.59245 1.50188C5.75917 1.16604 6.24083 1.16604 6.40755 1.50188L7.70631 4.11799L10.6107 4.54008C10.9834 4.59424 11.1319 5.04969 10.8621 5.31096L8.76087 7.34588L9.25677 10.2207C9.32046 10.5899 8.9307 10.8714 8.59727 10.6971L6 9.33899L3.40273 10.6971C3.0693 10.8714 2.67954 10.5899 2.74323 10.2207L3.23913 7.34588L1.13788 5.31096C0.868087 5.04969 1.01661 4.59424 1.38926 4.54008L4.29369 4.11799Z" stroke="#575757" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
		</svg>
	`;

	static filled = html`
		<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M4.29369 4.11799L5.59245 1.50188C5.75917 1.16604 6.24083 1.16604 6.40755 1.50188L7.70631 4.11799L10.6107 4.54008C10.9834 4.59424 11.1319 5.04969 10.8621 5.31096L8.76087 7.34588L9.25677 10.2207C9.32046 10.5899 8.9307 10.8714 8.59727 10.6971L6 9.33899L3.40273 10.6971C3.0693 10.8714 2.67954 10.5899 2.74323 10.2207L3.23913 7.34588L1.13788 5.31096C0.868087 5.04969 1.01661 4.59424 1.38926 4.54008L4.29369 4.11799Z" fill="#434343" stroke="#575757" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
		</svg>		
	`;

	static emptyStars = Array(5).fill(ReviewStars.empty);
	static filledStars = Array(5).fill(ReviewStars.filled);

	render() {
		return html`
			<div class="stars" style="--rating: ${this.rating}">
				<div class="empty">${ReviewStars.emptyStars}</div>
				<div class="filled">${ReviewStars.filledStars}</div>
			</div>
		`;
	}

	static styles = css`
		.stars {
			display: grid;
			justify-content: start;
		}

		.empty, .filled {
			display: flex;
			gap: 4px;
			grid-column: 1;
			grid-row: 1;
		}

		.filled {
			clip-path: inset(0 calc(100% - 100% * (var(--rating) / 5)) 0 0);
		}
	`;
}

customElements.define('review-stars', ReviewStars);


class ReviewAttributeRating extends LitElement {
	static properties = {
		title: { type: String },
		rating: { type: Number },
		minLabel: { type: String },
		maxLabel: { type: String }
	}

	render() {
		return html`
			<h3>${this.title}</h3>
			<span class="rating">${this.rating.toFixed(1)} out of 5</span>
			<div class="bar" aria-hidden="true" style="--progress: ${(this.rating / 5) * 100}%"></div>
			<div class="labels" aria-hidden="true">
				<span>${this.minLabel}</span>
				<span>${this.maxLabel}</span>
			</div>
		`;
	}

	static styles = css`
		${sharedStyles}

		:host {
			display: flex;
			flex-direction: column;
			gap: 4px;
			width: 100%;
			max-width: 350px;
		}

		.rating {
			position: absolute;
			width: 0px;
			height: 0px;
			font-size: 0px;
		}

		.bar {
			height: 4px;
			background-color: var(--color-bg-secondary);
			border-radius: 2px;
			overflow: hidden;
		}

		.bar::after {
			content: '';
			display: block;
			height: 100%;
			width: var(--progress);
			background-color: var(--color-bg-inverted-tertiary, #767676);
		}

		.labels {
			font: var(--text-sm);
			display: flex;
			justify-content: space-between;
		}

		.labels span:first-child {
			color: var(--color-text-disabled);
		}

		@media screen and (min-width: 992px) {
			:host {
				width: 245px;
			}
		}
	`
}

customElements.define('review-attribute-rating', ReviewAttributeRating);


class ReviewsDropdown extends LitElement {
	static properties = {
		options: { type: Array },
		selected: { type: String }
	}

	constructor() {
		super();

		this.options = [];
		this.selected = null;
	}

	willUpdate(changedProperties) {
		if (changedProperties.has('options') && this.options.length > 0 && this.selected == null) {
			this.selected = this.options[0].value;
		}
	}

	updated(changedProperties) {
		if (changedProperties.has('selected') && this.selected != null) {
			this.shadowRoot.querySelector('slide-out-modal').close();
			this.shadowRoot.querySelector('select').value = this.selected;
			this.dispatchEvent(new CustomEvent('change', { detail: { value: this.selected } }));
		}
	}

	render() {
		if (this.selected == null) return html``;

		const selectedName = this.options.find(option => option.value == this.selected).name;

		return html`
			<select @change=${e => this.selected = e.target.value}>
				${this.options.map(option => html`
					<option value=${option.value} ?selected=${this.selected == option.value} ?disabled=${option.disabled}>${option.name}</option>
				`)}
			</select>
			<div aria-hidden="true">
				<button class="dropdown" @click=${() => this.shadowRoot.querySelector('slide-out-modal').toggle()}>${selectedName}</button>
				<slide-out-modal desktop="dropdown" no-padding dropdown-width="max-content">
					<div class="content">
						<h3>Customer Reviews for</h3>
						${this.options.map(option => html`
							<button 
								class="option ${option.value == this.selected ? 'selected' : ''}"
								@click=${() => this.selected = option.value}
								?disabled=${option.disabled}
							>
								${option.name}
							</button>
						`)}
					</div>
				</slide-out-modal>
			</div>
		`;
	}

	static styles = css`
		${sharedStyles}

		:host {
			position: relative;
		}

		.dropdown {
			padding-block: var(--padding-xxs);
			display: flex;
			align-items: center;
			gap: 8px;
			font: var(--text-lg);
			border-bottom: 1px solid var(--color-border-button-primary, #BDBDBD);
		}

		.dropdown::after {
			content: '';
			display: block;
			width: 24px;
			height: 24px;
			background: var(--icon-arrow-down);
			transition: transform 150ms;
		}

		.modal-open .dropdown::after {
			transform: scaleY(-1);
		}

		select {
			position: absolute;
			width: 0;
			height: 0;
			overflow: hidden;
			appearance: none;
		}

		.content {
			padding: var(--padding-sm);
			padding-top: 0;
		}

		h3 {
			font: var(--title-sm);
			margin-block: var(--padding-xs);
			text-align: center;
		}

		.option {
			padding-block: var(--padding-xs);
			padding-inline: var(--padding-sm);
			display: flex;
			align-items: center;
			gap: var(--padding-xs);
			color: var(--color-text-tertiary, #575757);
			width: 100%;
		}

		.option[disabled] {
			opacity: 0.5;
			pointer-events: none;
		}

		.option.selected {
			color: var(--color-text-primary, #212121);
			pointer-events: none;
		}

		.option::before {
			content: '';
			display: block;
			width: 16px;
			height: 16px;
		}

		.option.selected::before {
			background: var(--icon-check);
		}

		@media (hover: hover) {
			.option:hover {
				background: rgba(0, 0, 0, 0.04);
			}
		}

		.option:active {
			background: rgba(0, 0, 0, 0.08);
		}

		@media (min-width: 992px) {
			.dropdown {
				font: var(--title-sm);
			}

			h3 {
				display: none;
			}

			.content {
				padding: 0;
				background: var(--color-bg-secondary, #F2F2F2);
				border-radius: var(--spacing-radious-radius-sm, 2px);
				border: 1px solid var(--color-border-border-primary, #E1E1E1);
			}
		}
	`;
}

customElements.define('reviews-dropdown', ReviewsDropdown);